import * as React from 'react'

import { TimesheetReferenceContractsDocument } from 'generated/resources/graphql'
import moment from 'moment'

import { observer } from 'mobx-react'

import EmptyList from 'modules/core/components/EmptyList'
import ErrorBoundary from 'modules/core/components/ErrorBoundary'
import Loading from 'modules/core/components/Loading'
import NotFound from 'modules/core/components/NotFound'
import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { useQuery } from 'modules/core/hooks/useQuery'
import hasFeature from 'modules/core/lib/hasFeature'
import i18n from 'modules/core/lib/i18n'
import companies from 'modules/core/store/collections/companies'

import TimesheetButtons from 'modules/attendance/components/ClockIn/AdminLayout/WeeklyView/TimesheetButtons'
import type Store from 'modules/attendance/components/ClockIn/Store'
import { useGetAttendanceBreakConfigurationsRange } from 'modules/attendance/hooks/useGetAttendanceBreakConfigurationsRange'
import { AttendanceBreaksProvider } from 'modules/attendance/lib/attendanceBreaksContext'
import { useAttendanceDateNavigator } from 'modules/attendance/lib/attendanceDateNavigatorContext'
import { BlockAutoScrollProvider } from 'modules/attendance/lib/blockAutoScrollContext'
import {
  TimesheetDataProvider,
  useTimesheetData,
} from 'modules/attendance/lib/timesheetDataContext'
import { TimesheetFeatures } from 'modules/attendance/lib/timesheetFeatures'
import { useGetBreakConfigurations } from 'modules/time_categories_settings/hooks/useGetBreakConfigurations'

import Layout from './Layout'
import Shifts from './Shifts'

interface Props {
  loading: boolean
  store: Store
  canReadOwnedAttendance: boolean
}

const ClockIn = ({
  loading,
  store,
  children,
  canReadOwnedAttendance,
}: React.PropsWithChildren<Props>) => {
  const { employee } = useCurrentSession()
  const { stringDateFrom: startOn, stringDateTo: endOn } = useAttendanceDateNavigator()

  const attendanceEmployeesPolicyId = employee?.attendanceEmployeesPolicyId
  const { loading: loadingRangeBreaks, breakConfigurationsRange: attendanceBreaks } =
    useGetAttendanceBreakConfigurationsRange(attendanceEmployeesPolicyId, startOn, endOn)

  const today = moment().format('YYYY-MM-DD')
  const { loading: loadingTodayBreaks, breakConfigurationsRange: clockinAttendanceBreaks } =
    useGetAttendanceBreakConfigurationsRange(attendanceEmployeesPolicyId, today, today)
  const { loading: loadingAllBreaks, breakConfigurations: allBreaks } = useGetBreakConfigurations(
    {}
  )

  const hasForfaitJour = hasFeature(TimesheetFeatures.FORFAIT_JOUR)
  const hasComplementaryHours = hasFeature(TimesheetFeatures.COMPLEMENTARY_HOURS)

  const { data: referenceContractData, loading: loadingReferenceContract } = useQuery(
    TimesheetReferenceContractsDocument,
    {
      variables: {
        employeeIds: [store.employeeId],
        referenceOn: moment().format('YYYY-MM-DD'),
      },
      skip: !hasForfaitJour && !hasComplementaryHours,
    }
  )

  const contract = referenceContractData?.contracts.referenceContractsConnection.nodes[0]

  const [loadingTimesheet, setLoadingTimesheet] = React.useState(false)

  const todayBreaks = attendanceBreaks.get(today) ?? []
  const loadingBreaks =
    loadingRangeBreaks || loadingAllBreaks || loadingTodayBreaks || loadingTimesheet
  const loadingData = loading || loadingTimesheet || loadingBreaks || loadingReferenceContract

  const currentCompany = companies.current

  const RenderTable = ({
    showHourBalance,
    isDaily,
  }: {
    showHourBalance: boolean
    isDaily: boolean
  }) => {
    const { attendancePeriods } = useTimesheetData()

    if (!attendancePeriods?.length) {
      return (
        <>
          {isDaily && (
            <TimesheetButtons
              employeeId={store.employeeId}
              showHourBalance={showHourBalance}
              isDaily
            />
          )}
          <EmptyList description={i18n.t('clock_in.empty_list.description')} />
        </>
      )
    }

    return (
      <ErrorBoundary>
        <Shifts store={store} showHourBalance={showHourBalance} isDaily={isDaily} />
      </ErrorBoundary>
    )
  }

  if (!currentCompany.get('has_attendance')) {
    return <NotFound />
  }

  const hasHourBalance = hasFeature('dev_hour_balance_ui')
  const canSeeHourBalance = hasFeature('hour_balance_visibility')
  const showHourBalance = hasHourBalance && canSeeHourBalance
  const isDaily = hasForfaitJour && !!contract?.frForfaitJours
  const isPartTime = !!contract?.partTime

  return (
    <AttendanceBreaksProvider
      value={{
        allBreaks,
        attendanceBreaks,
        todayBreaks,
        loadingBreaks,
        clockinAttendanceBreaks: clockinAttendanceBreaks.get(today) ?? [],
      }}
    >
      <TimesheetDataProvider
        employeeId={store.employeeId}
        companyId={currentCompany.id}
        setLoading={setLoadingTimesheet}
        isPartTime={isPartTime}
      >
        <BlockAutoScrollProvider
          value={{
            blockAutoScroll: false,
            setBlockAutoScroll: (_newBlockAutoScroll: boolean) => null,
          }}
        >
          <Layout isDaily={isDaily} showHourBalance={showHourBalance}>
            {loadingData && <Loading />}
            {children}
            {!loadingData && canReadOwnedAttendance && (
              <RenderTable showHourBalance={showHourBalance} isDaily={isDaily} />
            )}
          </Layout>
        </BlockAutoScrollProvider>
      </TimesheetDataProvider>
    </AttendanceBreaksProvider>
  )
}

export default observer(ClockIn)
